import { findImportBindingForLocalName } from '../../symbol-index/sqlite/symbol-index-import-sqlite.js';
import type { DbImportRow } from '../../symbol-index/sqlite/symbol-index-import-sqlite.js';
import type { DbTagRow } from '../types.js';
import { isConfidentNavigation, sortDefinitionsForDisplay } from './confidence.js';
import { followImportBinding } from './import-target.js';
import { resolveLocalDefinitionCandidates, resolveLocalDefinitions } from './local-definitions.js';
import type { NavigationContext, NavigationResult } from './types.js';

function findImportBindingForReference(ctx: NavigationContext): DbImportRow | null {
  return findImportBindingForLocalName(ctx.fileImports, ctx.ref.name, ctx.ref);
}

function resolveImportDefinitions(ctx: NavigationContext, binding: DbImportRow): DbTagRow[] {
  return followImportBinding(
    binding,
    ctx.lookupDefsInFileNamed,
    ctx.lookupImportsForFile,
    ctx.workspaceFiles,
    ctx.ref,
  );
}

function resolveLocalTargets(ctx: NavigationContext): DbTagRow[] {
  const primary = resolveLocalDefinitions(ctx.ref, ctx.fileDefs);
  if (primary.length > 0) return primary;
  return resolveLocalDefinitionCandidates(ctx.ref, ctx.fileDefs);
}

/**
 * Resolve navigation targets for a symbol reference using the symbol, import, and scope indices.
 */
export function resolveNavigation(ctx: NavigationContext): NavigationResult {
  if (ctx.ref.role === 'def') {
    return { definitions: [ctx.ref], confidentTarget: true, source: 'definition' };
  }

  if (ctx.ref.kind === 'module' && ctx.ref.nav_target_path) {
    return { definitions: [], confidentTarget: true, source: 'module' };
  }

  const importBinding = findImportBindingForReference(ctx);
  if (importBinding) {
    const importDefs = resolveImportDefinitions(ctx, importBinding);
    if (importDefs.length > 0) {
      const definitions = sortDefinitionsForDisplay(ctx.ref, ctx.refFile, importDefs);
      return {
        definitions,
        confidentTarget: isConfidentNavigation(definitions, 'import', ctx.ref),
        source: 'import',
      };
    }
  }

  const definitions = sortDefinitionsForDisplay(ctx.ref, ctx.refFile, resolveLocalTargets(ctx));
  return {
    definitions,
    confidentTarget: isConfidentNavigation(definitions, 'local', ctx.ref),
    source: 'local',
  };
}

export function refHasNavigationTargets(ctx: NavigationContext): boolean {
  if (ctx.ref.role === 'def') return true;
  if (ctx.ref.nav_target_path) return true;
  return resolveNavigation(ctx).definitions.length > 0;
}
